import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { FileDown, FileText, Upload, Loader2, Search, RefreshCcw } from "lucide-react";
import { adminApi } from "../lib/api";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./ui/table";

const fmtNum = (v, digits = 2) =>
  v === null || v === undefined ? "—" : new Intl.NumberFormat("ru-RU", { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(v);
const fmtDate = (v) => (v ? new Date(v).toLocaleString("ru-RU") : "—");
const shortId = (v) => (v ? `${String(v).slice(0, 8)}…` : "—");

const STATUS_FILTERS = [
  { value: "paid", label: "Оплачены" },
  { value: "", label: "Все" },
  { value: "pending", label: "Ожидают" },
];

const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
};

export const ReportsTab = () => {
  const todayStr = new Date().toISOString().slice(0, 10);
  const monthStartStr = todayStr.slice(0, 8) + "01";
  const [dateFrom, setDateFrom] = useState(monthStartStr);
  const [dateTo, setDateTo] = useState(todayStr);
  const [phonesFilter, setPhonesFilter] = useState("");
  const [status, setStatus] = useState("paid");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState("");
  const importRef = useRef(null);

  const params = useCallback(() => ({
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    phones: phonesFilter.trim() || undefined,
    status: status || undefined,
  }), [dateFrom, dateTo, phonesFilter, status]);

  const applyPreset = (preset) => {
    const today = new Date().toISOString().slice(0, 10);
    if (preset === "today") {
      setDateFrom(today);
      setDateTo(today);
    } else if (preset === "week") {
      setDateFrom(new Date(Date.now() - 6 * 864e5).toISOString().slice(0, 10));
      setDateTo(today);
    } else {
      setDateFrom(today.slice(0, 8) + "01");
      setDateTo(today);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.get("/admin/reports", { params: { ...params(), limit: 200 } });
      setRows(res.data.items);
      setTotal(res.data.total);
      setSelected(new Set());
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Не удалось загрузить отчёты");
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const toggleAll = () =>
    setSelected((s) => (s.size === rows.length ? new Set() : new Set(rows.map((r) => r.order_id))));

  const download = async (kind, format) => {
    setBusy(kind + format);
    try {
      const url = kind === "fill" ? "/admin/reports/fill-template" : "/admin/reports/export";
      const p = { ...params() };
      if (kind === "export") {
        p.format = format;
        if (selected.size > 0) p.ids = [...selected].join(",");
      }
      const res = await adminApi.get(url, { params: p, responseType: "blob" });
      const ext = kind === "fill" ? "xlsx" : format;
      saveBlob(res.data, `velowallet-${kind === "fill" ? "fill" : "report"}_${dateFrom || "all"}_${dateTo || "all"}.${ext}`);
      toast.success("Файл сформирован");
    } catch (err) {
      toast.error("Не удалось сформировать файл");
    } finally {
      setBusy("");
    }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (importRef.current) importRef.current.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Нужен файл Excel в формате .xlsx");
      return;
    }
    setBusy("import");
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await adminApi.post("/admin/reports/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const d = res.data;
      toast.success(`Обновлено операций: ${d.updated}` +
        (d.not_found_count ? `, не найдено: ${d.not_found_count}` : "") +
        (d.problems_count ? `, ошибок в ячейках: ${d.problems_count}` : ""));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Не удалось загрузить файл");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="flex flex-col gap-3" data-testid="reports-tab">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1">
          <Button data-testid="report-preset-today" variant="outline" size="sm" onClick={() => applyPreset("today")}>Сегодня</Button>
          <Button data-testid="report-preset-week" variant="outline" size="sm" onClick={() => applyPreset("week")}>Неделя</Button>
          <Button data-testid="report-preset-month" variant="outline" size="sm" onClick={() => applyPreset("month")}>Месяц</Button>
        </div>
        <Input data-testid="report-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-36 h-9" />
        <span className="text-muted-foreground text-sm">—</span>
        <Input data-testid="report-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-36 h-9" />
        <div className="relative">
          <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="report-phones-input"
            placeholder="Телефоны через запятую"
            value={phonesFilter}
            onChange={(e) => setPhonesFilter(e.target.value)}
            className="pl-8 w-60 h-9"
          />
        </div>
        <div className="flex gap-1">
          {STATUS_FILTERS.map((f) => (
            <Button
              key={f.value}
              data-testid={`report-filter-${f.value || "all"}`}
              variant={status === f.value ? "default" : "outline"}
              size="sm"
              className={status === f.value ? "bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" : ""}
              onClick={() => setStatus(f.value)}
            >
              {f.label}
            </Button>
          ))}
        </div>
        <Button data-testid="reports-refresh-button" variant="outline" size="icon" className="h-9 w-9" onClick={load} disabled={loading}>
          <RefreshCcw size={15} className={loading ? "animate-spin" : ""} />
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          data-testid="report-export-xlsx-button"
          size="sm"
          onClick={() => download("export", "xlsx")}
          disabled={!!busy}
          className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 gap-1.5 h-9"
        >
          {busy === "exportxlsx" ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />}
          Скачать Excel{selected.size > 0 ? ` (${selected.size})` : ""}
        </Button>
        <Button
          data-testid="report-export-pdf-button"
          size="sm"
          variant="outline"
          onClick={() => download("export", "pdf")}
          disabled={!!busy}
          className="gap-1.5 h-9"
        >
          {busy === "exportpdf" ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />}
          Скачать PDF{selected.size > 0 ? ` (${selected.size})` : ""}
        </Button>
        <div className="w-px h-6 bg-border mx-1" />
        <Button
          data-testid="report-fill-template-button"
          size="sm"
          variant="outline"
          onClick={() => download("fill", "xlsx")}
          disabled={!!busy}
          className="gap-1.5 h-9"
        >
          {busy === "fillxlsx" ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />}
          Файл на заполнение
        </Button>
        <input ref={importRef} type="file" accept=".xlsx" className="hidden" data-testid="report-import-input" onChange={onImport} />
        <Button
          data-testid="report-import-button"
          size="sm"
          variant="outline"
          onClick={() => importRef.current?.click()}
          disabled={!!busy}
          className="gap-1.5 h-9"
        >
          {busy === "import" ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
          Загрузить заполненный
        </Button>
        <span className="text-xs text-muted-foreground ml-auto">
          Найдено: {total}{selected.size > 0 ? ` · выбрано: ${selected.size}` : ""}
        </span>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table data-testid="admin-reports-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">
                  <Checkbox
                    data-testid="report-select-all"
                    checked={rows.length > 0 && selected.size === rows.length}
                    onCheckedChange={toggleAll}
                  />
                </TableHead>
                <TableHead>№</TableHead>
                <TableHead>Дата</TableHead>
                <TableHead>Client ID</TableHead>
                <TableHead className="text-right">Сумма ₽</TableHead>
                <TableHead className="text-right">Комиссия NORD ₽</TableHead>
                <TableHead className="text-right">Чистая ₽</TableHead>
                <TableHead className="text-right">Курс</TableHead>
                <TableHead className="text-right">USDT</TableHead>
                <TableHead>TXID</TableHead>
                <TableHead>TRON</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} className="text-center text-muted-foreground py-8">
                    {loading ? "Загрузка…" : "Нет операций за выбранный период"}
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.order_id} data-testid="admin-report-row">
                  <TableCell>
                    <Checkbox
                      data-testid="report-row-checkbox"
                      checked={selected.has(r.order_id)}
                      onCheckedChange={() => toggle(r.order_id)}
                    />
                  </TableCell>
                  <TableCell className="tabular-nums font-medium">{r.operation_number || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(r.created_at_utc)}</TableCell>
                  <TableCell className="tabular-nums whitespace-nowrap">{r.client_id_masked}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{fmtNum(r.amount_rub, 0)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtNum(r.nord_commission_rub)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtNum(r.net_rub)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtNum(r.exec_rate ?? r.rate_client)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtNum(r.usdt_amount ?? r.estimated_usdt)}</TableCell>
                  <TableCell className="text-xs font-mono" title={r.txid || ""}>{shortId(r.txid)}</TableCell>
                  <TableCell>
                    {r.tx_status ? (
                      <Badge variant="outline" className="border-[hsl(var(--success))] text-[hsl(var(--success))]">{r.tx_status}</Badge>
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <p className="text-xs text-muted-foreground">
        Отчёт по п. 12 оферты. Комиссия NORD 15%. Данные исполнения обмена
        (фактический курс, USDT, TXID) заполняются через «Файл на заполнение» → «Загрузить заполненный».
        Выберите строки галочками, чтобы скачать отчёт по одному или нескольким клиентам.
      </p>
    </div>
  );
};
